import React from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import SpinWheel from "@/components/SpinWheel";
import SpinWheelPremium from "@/components/SpinWheelPremium";
import { WheelThemePresets } from "@/components/WheelThemePresets";

const DEFAULT_RIM = "#1F4D3A";

function DesignCard({ active, onClick, label, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={`rounded-xl border-2 p-2.5 text-left transition-colors ${
        active ? "border-violet-500 bg-violet-500/5" : "border-border hover:border-violet-300"
      }`}
    >
      <div className="w-full max-w-[120px] mx-auto aspect-[400/440] flex items-center">{children}</div>
      <div className="mt-2 text-[12px] font-semibold text-center">{label}</div>
    </button>
  );
}

// Admin: pick the spin-wheel look per game + customise the "Nouveau design".
export function WheelDesignPicker({ game, patch }) {
  const { t } = useTranslation();
  const premium = game.wheel_design === "premium";
  const showNames = game.wheel_show_prize_names !== false;
  const segments = game.segments || [];
  const choosePremium = () =>
    patch({
      wheel_design: "premium",
      ...(game.wheel_curved_text == null
        ? { wheel_curved_text: t("game_editor.wheel_curved_text_default") }
        : {}),
    });
  return (
    <div className="pt-2 border-t border-border/60" data-testid="wheel-design-section">
      <label className="text-xs font-medium block mb-1">{t("game_editor.wheel_design")}</label>
      <p className="text-[11px] text-muted-foreground leading-snug mb-2">{t("game_editor.wheel_design_hint")}</p>
      <WheelThemePresets game={game} patch={patch} />
      <div className="grid grid-cols-2 gap-2.5">
        <DesignCard active={!premium} onClick={() => patch({ wheel_design: "classic" })} label={t("game_editor.wheel_design_classic")} testId="wheel-design-classic">
          <SpinWheel segments={segments} labelOrientation={game.wheel_label_orientation || "horizontal"} showPrizeNames={showNames} preview />
        </DesignCard>
        <DesignCard active={premium} onClick={choosePremium} label={t("game_editor.wheel_design_premium")} testId="wheel-design-premium">
          <SpinWheelPremium segments={segments} rimColor={game.wheel_rim_color || DEFAULT_RIM} curvedText={game.wheel_curved_text} pointerColor={game.button_bg_color || "#D62828"} showPrizeNames={showNames} preview />
        </DesignCard>
      </div>
      <div className="mt-3 flex items-start justify-between gap-3">
        <div>
          <label className="text-xs font-medium">{t("game_editor.wheel_show_prize_names")}</label>
          <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{t("game_editor.wheel_show_prize_names_hint")}</p>
        </div>
        <Switch
          checked={showNames}
          onCheckedChange={(v) => patch({ wheel_show_prize_names: !!v })}
          data-testid="wheel-show-prize-names-toggle"
        />
      </div>
      {premium && (
        <div className="mt-3 space-y-3 rounded-lg border border-border/60 bg-muted/40 p-3" data-testid="wheel-premium-options">
          <div>
            <label className="text-[11px] text-muted-foreground block mb-1">{t("game_editor.wheel_rim_color")}</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={game.wheel_rim_color || DEFAULT_RIM}
                onChange={(e) => patch({ wheel_rim_color: e.target.value })}
                className="h-9 w-12 rounded-lg border border-border cursor-pointer bg-transparent"
                data-testid="wheel-rim-color-input"
              />
              <span className="text-[11px] font-mono text-muted-foreground">{game.wheel_rim_color || DEFAULT_RIM}</span>
            </div>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground block mb-1">{t("game_editor.wheel_curved_text")}</label>
            <Input
              value={game.wheel_curved_text || ""}
              maxLength={40}
              onChange={(e) => patch({ wheel_curved_text: e.target.value })}
              placeholder={t("game_editor.wheel_curved_text_default")}
              className="rounded-xl h-9"
              data-testid="wheel-curved-text-input"
            />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground block mb-1">{t("game_editor.wheel_button_label")}</label>
            <Input
              value={game.wheel_button_label || ""}
              maxLength={24}
              onChange={(e) => patch({ wheel_button_label: e.target.value })}
              placeholder={t("play.wheel_play_button")}
              className="rounded-xl h-9"
              data-testid="wheel-button-label-input"
            />
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug">{t("game_editor.wheel_premium_segments_hint")}</p>
        </div>
      )}
    </div>
  );
}
